import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import { DateFilter } from "components/form_components/Inputs";
import { MentionsAwardsFilter } from "components/form_components/MentionsAwardsFilter";
import { useSignalSettings } from "lib/hooks/api/teams/useSignalSettings";
import { useSubscription } from "lib/hooks/subscription";
import { createFeedFilterChangedEvents } from "lib/savedViewTracking";
import { useTracking } from "lib/tracking";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "../../lib/featureFlags";
import { useFormAutoSubmit } from "../../lib/hooks/useFormAutoSubmit";
import { useURLState } from "../../lib/hooks/useURLState";
import { camelToSentence, isObjEmpty } from "../../lib/utils";
import FilterCollapse from "../filter_form/filter_collapse/FilterCollapse";
import FilterCollapseHeader from "../filter_form/filter_collapse_header/FilterCollapseHeader";
import FilterFormTitle from "../filter_form/filter_form_title/FilterFormTitle";
import { countFilters } from "../filter_form/utils";
import { noticeFilterContents } from "./filter_groups";
import { EMPTY_FILTERS, getAppliedFilters, NoticeFilters, NoticeFiltersSchema } from "./utils";

import css from "./FilterForm.module.scss";

export const groupedKeys: Record<FilterGroupKeys, (keyof NoticeFilters)[]> = {
  keywords: ["keywords", "excludeKeywords"],
  // TODO: Remove signals filter from UI post layer cake
  signals: ["signals"],
  procurementStage: ["stage"],
  buyers: ["buyers", "buyerListIds", "buyerCategories", "buyerCountryRegions"],
  suppliers: ["suppliers", "supplierSme", "supplierGuids", "supplierMentionType"],
  contractValue: ["value"],
  contractDates: ["closeDate", "expiryDate", "publishDate"],
  signalScore: ["relevanceScore"],
  frameworks: ["frameworkIds", "frameworkFilterOptions"],
  cpvCode: ["cpvCodes"],
  noticeLists: ["noticeLists"],
  hiddenNotices: ["includeHiddenNotices"],
};

type FilterGroupKeys = keyof typeof noticeFilterContents;

export type FilterFormLocation = "my_feed" | "buyers" | "suppliers" | "call-offs";

export type FilterOptions = {
  disabledFields?: (keyof NoticeFilters)[];
  disabledSections?: FilterGroupKeys[];
  hiddenFilters?: (keyof NoticeFilters)[];
};

type FilterFormProps = {
  onChange: (filters: NoticeFilters) => void;
  filters: NoticeFilters;
  // Base filters that "Clear all" resets to. Defaults to EMPTY_FILTERS, but a
  // scoped table (e.g. a buyer profile) passes its own defaults to preserve hidden scope.
  defaultFilters?: NoticeFilters;
  options?: FilterOptions;
  onClose: () => void;
  activeGroups: string[];
  onActiveGroupsChange: (value: string[]) => void;
  location?: FilterFormLocation;
  isSidebar?: boolean;
  isLoadingResults?: boolean;
  totalResults?: number;
  resetKey?: string;
  setSelectedView?: (viewId?: string, viewName?: string, context?: string | undefined) => void;
  isEmptyState?: boolean;
  onSideBarOpenChange?: (open: boolean) => void;
};

export function FilterForm({
  onChange,
  filters,
  defaultFilters = EMPTY_FILTERS,
  options,
  location,
  onClose,
  activeGroups,
  onActiveGroupsChange,
  isSidebar,
  resetKey,
  setSelectedView,
  isEmptyState,
  onSideBarOpenChange,
}: FilterFormProps) {
  const { logEvents } = useTracking();
  const previous = useRef(filters);
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  const {
    control,
    watch,
    formState: { touchedFields },
    trigger,
    getValues,
    setValue,
    reset,
  } = useForm<NoticeFilters>({
    mode: "onChange",
    defaultValues: filters,
  });

  useEffect(() => {
    // Because the filter form is no longer de-rendered when the view changes, we need to
    // reset the form when the view changes.
    if (resetKey) {
      reset(filters);
    }
  }, [resetKey, filters, reset]);

  const data = watch();

  const subscription = useSubscription();

  const [, setNoticeFilters] = useURLState(
    "noticeFilters",
    undefined,
    NoticeFiltersSchema.optional(),
    { encoder: "compress" },
  );

  const displayFrameworksSection = !options?.disabledSections?.includes("frameworks");

  const { data: signalSettings } = useSignalSettings();

  const onChangeWithLogging = (currentValidatedData: NoticeFilters) => {
    logEvents(
      ...createFeedFilterChangedEvents(previous.current, currentValidatedData, signalSettings),
    );
    previous.current = currentValidatedData;
    onChange(currentValidatedData);
  };

  useFormAutoSubmit({
    watch,
    trigger,
    getValues,
    onChange: onChangeWithLogging,
  });

  function hideNullsWhenSet(fieldName: "closeDate" | "expiryDate", value: DateFilter) {
    // don't set when clearing a field
    const hasTransitionedToTruthy = Object.keys(value).some((field) => {
      const prevFieldValue = previous.current?.[fieldName]?.filter;
      return isObjEmpty(prevFieldValue) && value[field];
    });

    // don't set if checkbox has been changed manually already
    if (hasTransitionedToTruthy && !touchedFields[fieldName]?.hideNulls) {
      setValue(`${fieldName}.hideNulls`, true);
    }
  }

  const counts = countFilters(filters, groupedKeys);
  counts.signals = filters.signals.length || 0;

  function clearFilter(key: FilterGroupKeys) {
    const keys = groupedKeys[key];
    keys.forEach((k) => {
      setValue(k, EMPTY_FILTERS[k]);
    });

    // When a user has not made any changes to the form, the clearFilter will not trigger a change event.
    // This is because the form is not dirty.
    // We need to trigger the change event manually in this situation.
    void trigger().then((isValid) => {
      if (isValid) {
        onChangeWithLogging(getValues());
      }
    });
  }

  const filterProps = {
    control,
    data,
    options,
    hideNullsWhenSet,
  };

  function getFilterGroups() {
    let items = (Object.keys(groupedKeys) as FilterGroupKeys[]).map((key) => {
      const ContentComponent = noticeFilterContents[key];

      return {
        key,
        header: (
          <FilterCollapseHeader
            title={key === "cpvCode" ? "CPV code" : camelToSentence(key)}
            count={counts[key]}
            clear={() => clearFilter(key)}
            paywallIcon={key === "suppliers" && !subscription.hasDataTypes("SUPPLIERS")}
          />
        ),
        content: <ContentComponent {...filterProps} />,
      };
    });

    if (!displayFrameworksSection) items = items.filter((item) => item.key !== "frameworks");

    const disabledSections = options?.disabledSections || [];
    items = items.filter((item) => !disabledSections.includes(item.key));

    if (options?.hiddenFilters?.includes("suppliers")) {
      items = items.filter((item) => item.key !== "suppliers");
    }

    // TODO: Remove signals filter from UI post layer cake
    items = items.filter((item) => item.key !== "signals");

    if (isRelevancySearchEnabled) items = items.filter((item) => item.key !== "signalScore");

    return items;
  }

  const items = getFilterGroups();

  function clearAll() {
    reset({ ...defaultFilters });

    setNoticeFilters(defaultFilters);
    setSelectedView && setSelectedView(undefined);
  }

  return (
    <form className={css.form} aria-label="Notice filters">
      <FilterFormTitle
        onClose={onClose}
        isSidebar={isSidebar}
        isEmptyState={isEmptyState}
        showClear={Object.values(counts).some(Boolean)}
        clearFilters={clearAll}
        filtersCount={getAppliedFilters(filters).length}
        onSideBarOpenChange={onSideBarOpenChange}
      />
      {location === "suppliers" && (
        <div className={css.mentionsAwardsWrapper}>
          <MentionsAwardsFilter control={control} isSingleSupplier />
        </div>
      )}
      <FilterCollapse items={items} value={activeGroups} onChange={onActiveGroupsChange} />
    </form>
  );
}
